// Banco de "atividades prontas para imprimir": fichas simples, de uma página, sem
// preparo de material além de papel e lápis. Complementam o banco rico (atividades-v4)
// para o momento em que o professor precisa de algo imediato para aplicar e imprimir.
// Cada item usa o mesmo schema mínimo já lido pelo motor de recomendação
// (habilidades_alvo, necessidades_indicadas, faixa_etaria, nivel_dificuldade), então
// entra no mesmo ranqueamento por déficit/idade/dificuldade das demais atividades.
//
// `impressao` descreve o conteúdo imprimível de forma estruturada; quem sabe desenhar a
// ficha em PDF é src/lib/printableWorksheet.js.

// Ficha imprimível não é específica de diagnóstico (o alvo é a habilidade, não o laudo),
// então cobre todos os códigos de necessidade usados hoje no sistema — tanto as siglas
// curtas do cadastro do aluno (TEA, TDAH, DI, TA) quanto os códigos descritivos do banco
// rico (deficiencia_intelectual, dislexia etc.) — para não ficar de fora do ranqueamento
// por necessidade em nenhum dos dois formatos.
const NECESSIDADES_UNIVERSAIS = ['tea', 'tdah', 'di', 'ta', 'deficiencia_intelectual', 'transtorno_de_aprendizagem', 'dislexia', 'deficiencia_visual', 'surdez_libras', 'paralisia_cerebral', 'sindrome_de_down'];

function ficha(base) {
  return {
    pronta: true,
    necessidades_indicadas: NECESSIDADES_UNIVERSAIS,
    materiais: ['papel', 'lápis ou caneta'],
    fonte: 'Adaptado de práticas usuais de sala de recursos multifuncionais (AEE) — grafismo, contagem, discriminação visual e roteiros de aplicação.',
    ...base,
  };
}

const atividadesProntas = [
  ficha({
    id: 'pronta_cog_atencao_circular',
    titulo: 'Circule sempre a mesma letra',
    eixo: 'cognitivo',
    habilidades_alvo: ['cog_atencao'],
    faixa_etaria: { min: 5, max: 12 },
    nivel_dificuldade: 1,
    objetivo: 'Sustentar atenção visual em uma tarefa de busca simples.',
    instrucao: 'Peça para o aluno circular, o mais rápido possível, todas as vezes que aparecer a letra-alvo na grade.',
    impressao: { tipo: 'circular_igual', config: { alvo: 'A', distratores: ['A', 'B', 'O', 'A', 'C', 'A', 'D', 'A', 'B', 'A'], linhas: 6, colunas: 8 } },
  }),
  ficha({
    id: 'pronta_cog_memoria_roteiro',
    titulo: 'O que sumiu da mesa?',
    eixo: 'cognitivo',
    habilidades_alvo: ['cog_memoria'],
    faixa_etaria: { min: 4, max: 12 },
    nivel_dificuldade: 1,
    objetivo: 'Reter e recuperar uma informação visual de curto prazo.',
    instrucao: 'Mostre de 3 a 5 objetos por 10 segundos, cubra, retire um sem o aluno ver e pergunte o que sumiu.',
    impressao: {
      tipo: 'roteiro_aplicacao',
      config: {
        passos: [
          'Mostre de 3 a 5 objetos conhecidos por 10 segundos e diga "olhe bem".',
          'Cubra os objetos e retire 1 item sem o aluno perceber.',
          'Descubra e pergunte "o que sumiu?".',
          'Aumente a quantidade de objetos conforme os acertos.',
        ],
        registro: { colunas: ['Tentativa', 'Acertou sozinho', 'Acertou com dica', 'Não acertou'], linhas: 5 },
      },
    },
  }),
  ficha({
    id: 'pronta_cog_discriminacao_circular_forma',
    titulo: 'Ache a forma diferente',
    eixo: 'cognitivo',
    habilidades_alvo: ['cog_discriminacao'],
    faixa_etaria: { min: 4, max: 10 },
    nivel_dificuldade: 1,
    objetivo: 'Discriminar visualmente um estímulo entre distratores parecidos.',
    instrucao: 'Peça para circular, em cada linha, apenas a forma diferente das demais.',
    impressao: { tipo: 'circular_igual', config: { alvo: '●', distratores: ['■', '■', '●', '■', '▲', '■', '●', '■', '■', '▲'], linhas: 5, colunas: 8 } },
  }),
  ficha({
    id: 'pronta_cog_raciocinio_ligar_pontos',
    titulo: 'Ligue os pontos em ordem',
    eixo: 'cognitivo',
    habilidades_alvo: ['cog_raciocinio'],
    faixa_etaria: { min: 5, max: 11 },
    nivel_dificuldade: 2,
    objetivo: 'Organizar uma sequência lógica simples (1, 2, 3...) até formar uma figura.',
    instrucao: 'Peça para ligar os pontos numerados em ordem crescente, sem pular nenhum número.',
    impressao: { tipo: 'ligar_pontos', config: { forma: 'estrela', quantidadePontos: 10 } },
  }),
  ficha({
    id: 'pronta_cog_funcoes_exec_sequencia',
    titulo: 'Complete a sequência',
    eixo: 'cognitivo',
    habilidades_alvo: ['cog_funcoes_exec'],
    faixa_etaria: { min: 6, max: 12 },
    nivel_dificuldade: 2,
    objetivo: 'Identificar um padrão simples e completar os espaços que faltam.',
    instrucao: 'Peça para o aluno descobrir o padrão e escrever o número que falta em cada caixa vazia.',
    impressao: { tipo: 'sequencia_completar', config: { linhas: [['1', '2', '', '4', '5'], ['2', '', '6', '', '10'], ['1', '3', '5', '', '9']] } },
  }),
  ficha({
    id: 'pronta_aca_fonologica_silabas',
    titulo: 'Quantas sílabas tem a palavra?',
    eixo: 'academico',
    habilidades_alvo: ['aca_consciencia_fonologica'],
    faixa_etaria: { min: 5, max: 10 },
    nivel_dificuldade: 1,
    objetivo: 'Perceber e contar sílabas de palavras faladas (consciência fonológica).',
    instrucao: 'Fale a palavra bem devagar batendo palmas por sílaba; o aluno marca uma bolinha para cada palma na linha correspondente.',
    impressao: {
      tipo: 'roteiro_aplicacao',
      config: {
        passos: [
          'Escolha uma palavra da lista e fale bem devagar, batendo palmas por sílaba.',
          'Peça para o aluno repetir batendo palmas junto.',
          'Peça para marcar uma bolinha na linha da palavra para cada palma batida.',
          'Confira juntos contando as bolinhas em voz alta.',
        ],
        registro: { colunas: ['Palavra', 'Nº de sílabas marcadas'], linhas: ['boneca', 'sol', 'computador', 'pé', 'sapato'] },
      },
    },
  }),
  ficha({
    id: 'pronta_aca_leitura_tracar_palavras',
    titulo: 'Trace as palavras do dia a dia',
    eixo: 'academico',
    habilidades_alvo: ['aca_leitura', 'aca_escrita'],
    faixa_etaria: { min: 6, max: 12 },
    nivel_dificuldade: 2,
    objetivo: 'Reconhecer e reproduzir palavras funcionais do cotidiano.',
    instrucao: 'Peça para o aluno passar o lápis por cima de cada palavra, lendo em voz alta enquanto traça.',
    impressao: { tipo: 'tracar_caracteres', config: { itens: ['mamãe', 'escola', 'água', 'nome'], tamanho: 40 } },
  }),
  ficha({
    id: 'pronta_aca_escrita_grafismo',
    titulo: 'Trace as linhas até o fim',
    eixo: 'academico',
    habilidades_alvo: ['aca_escrita', 'psi_coordenacao_fina'],
    faixa_etaria: { min: 4, max: 9 },
    nivel_dificuldade: 1,
    objetivo: 'Praticar traços de pré-escrita (reta, onda, zigue-zague) antes das letras.',
    instrucao: 'Peça para passar o lápis sobre a linha pontilhada, da esquerda para a direita, sem tirar o lápis do papel.',
    impressao: { tipo: 'traco_livre', config: { padroes: ['reta', 'onda', 'zigue_zague', 'laco'] } },
  }),
  ficha({
    id: 'pronta_aca_matematica_contar',
    titulo: 'Conte e circule a quantidade',
    eixo: 'academico',
    habilidades_alvo: ['aca_matematica'],
    faixa_etaria: { min: 4, max: 9 },
    nivel_dificuldade: 1,
    objetivo: 'Contar quantidades pequenas e registrar o total.',
    instrucao: 'Peça para contar as bolinhas de cada linha em voz alta e escrever o total na caixa ao lado.',
    impressao: { tipo: 'contar_e_circular', config: { grupos: [3, 5, 7, 4] } },
  }),
  ficha({
    id: 'pronta_aca_matematica_sequencia',
    titulo: 'Complete a sequência numérica',
    eixo: 'academico',
    habilidades_alvo: ['aca_matematica', 'cog_raciocinio'],
    faixa_etaria: { min: 6, max: 12 },
    nivel_dificuldade: 2,
    objetivo: 'Reconhecer e continuar um padrão numérico simples.',
    instrucao: 'Peça para descobrir o que muda de uma caixa para outra e completar os espaços vazios.',
    impressao: { tipo: 'sequencia_completar', config: { linhas: [['1', '2', '3', '', '5'], ['5', '10', '', '20', '25'], ['10', '9', '', '7', '6']] } },
  }),
  ficha({
    id: 'pronta_psi_coordenacao_fina_traco',
    titulo: 'Traçado livre: da linha simples ao laço',
    eixo: 'psicomotor',
    habilidades_alvo: ['psi_coordenacao_fina'],
    faixa_etaria: { min: 4, max: 10 },
    nivel_dificuldade: 2,
    objetivo: 'Ganhar controle do traço em movimentos progressivamente mais complexos.',
    instrucao: 'Peça para traçar cada linha sem levantar o lápis, começando pela mais simples.',
    impressao: { tipo: 'traco_livre', config: { padroes: ['reta', 'zigue_zague', 'onda', 'laco'] } },
  }),
  ficha({
    id: 'pronta_psi_coordenacao_ampla_roteiro',
    titulo: 'Circuito de movimento',
    eixo: 'psicomotor',
    habilidades_alvo: ['psi_coordenacao_ampla'],
    faixa_etaria: { min: 4, max: 12 },
    nivel_dificuldade: 1,
    objetivo: 'Executar movimentos amplos com controle e segurança.',
    instrucao: 'Monte um pequeno circuito (pular, andar em linha reta, arremessar) e registre o desempenho em cada estação.',
    impressao: {
      tipo: 'roteiro_aplicacao',
      config: {
        passos: ['Pular com os dois pés juntos 5 vezes.', 'Andar sobre uma linha reta no chão sem sair da linha.', 'Arremessar uma bolinha dentro de uma caixa a curta distância.', 'Repetir a estação em que houve mais dificuldade.'],
        registro: { colunas: ['Estação', 'Conseguiu', 'Com apoio', 'Não conseguiu'], linhas: ['Pular', 'Andar na linha', 'Arremessar'] },
      },
    },
  }),
  ficha({
    id: 'pronta_psi_oculomanual_ligar_pontos',
    titulo: 'Ligue os pontos: quadrado',
    eixo: 'psicomotor',
    habilidades_alvo: ['psi_oculomanual'],
    faixa_etaria: { min: 4, max: 9 },
    nivel_dificuldade: 1,
    objetivo: 'Coordenar olhar e mão para conectar pontos em sequência.',
    instrucao: 'Peça para ligar os pontos numerados em ordem, com calma, sem pressa.',
    impressao: { tipo: 'ligar_pontos', config: { forma: 'quadrado', quantidadePontos: 4 } },
  }),
  ficha({
    id: 'pronta_psi_equilibrio_roteiro',
    titulo: 'Desafio do equilíbrio',
    eixo: 'psicomotor',
    habilidades_alvo: ['psi_equilibrio'],
    faixa_etaria: { min: 4, max: 12 },
    nivel_dificuldade: 1,
    objetivo: 'Manter postura e equilíbrio estático e dinâmico com segurança.',
    instrucao: 'Peça para ficar em um pé só, depois andar sobre uma linha; registre o tempo/quantidade de apoios.',
    impressao: {
      tipo: 'roteiro_aplicacao',
      config: {
        passos: ['Ficar em pé apoiado em uma perna só, contando até 5.', 'Repetir na outra perna.', 'Andar sobre uma linha reta no chão, braços abertos.', 'Aumentar o tempo/distância conforme a segurança do aluno.'],
        registro: { colunas: ['Tarefa', 'Conseguiu', 'Com apoio', 'Não conseguiu'], linhas: ['Perna direita', 'Perna esquerda', 'Linha reta'] },
      },
    },
  }),
  ficha({
    id: 'pronta_psi_esquema_corporal_contorno_mao',
    titulo: 'Contorne e nomeie sua mão',
    eixo: 'psicomotor',
    habilidades_alvo: ['psi_esquema_corporal'],
    faixa_etaria: { min: 4, max: 9 },
    nivel_dificuldade: 1,
    objetivo: 'Reconhecer partes do corpo e sua representação no papel.',
    instrucao: 'Peça para apoiar a mão no papel, contornar com o lápis e depois nomear cada dedo contornado.',
    impressao: {
      tipo: 'roteiro_aplicacao',
      config: {
        passos: ['Apoie a mão espalmada sobre o papel (no espaço abaixo).', 'Contorne toda a mão com o lápis, sem mexer os dedos.', 'Aponte e nomeie cada dedo contornado: polegar, indicador, médio, anelar, mínimo.', 'Repita com a outra mão para comparar o tamanho.'],
        espacoDesenho: true,
      },
    },
  }),
  ficha({
    id: 'pronta_soc_interacao_roteiro',
    titulo: 'Jogo da vez: espera e turnos',
    eixo: 'social',
    habilidades_alvo: ['soc_interacao'],
    faixa_etaria: { min: 4, max: 12 },
    nivel_dificuldade: 1,
    objetivo: 'Participar de uma troca social simples respeitando turnos.',
    instrucao: 'Use um cartão "minha vez / sua vez" em um jogo simples de 2 jogadores e registre quantos turnos o aluno esperou.',
    impressao: {
      tipo: 'roteiro_aplicacao',
      config: {
        passos: ['Explique a regra: só joga quem está com o cartão "minha vez".', 'Jogue uma rodada simples (empilhar peças, jogo de cartas) alternando o cartão.', 'Elogie especificamente cada espera bem-sucedida.', 'Aumente o número de turnos aos poucos.'],
        registro: { colunas: ['Turno', 'Esperou sozinho', 'Precisou de lembrete'], linhas: 6 },
      },
    },
  }),
  ficha({
    id: 'pronta_soc_comunicacao_roteiro',
    titulo: 'Peça, escolha, comente',
    eixo: 'social',
    habilidades_alvo: ['soc_comunicacao'],
    faixa_etaria: { min: 3, max: 12 },
    nivel_dificuldade: 1,
    objetivo: 'Expressar pedidos, escolhas e comentários de forma funcional (fala, gesto, Libras ou CAA).',
    instrucao: 'Ofereça 2 opções concretas (ex.: dois brinquedos) e espere uma iniciativa de comunicação antes de entregar.',
    impressao: {
      tipo: 'roteiro_aplicacao',
      config: {
        passos: ['Mostre 2 opções concretas e pergunte "qual você quer?".', 'Espere de 5 a 8 segundos por qualquer forma de comunicação (fala, gesto, apontar, cartão).', 'Entregue imediatamente após qualquer tentativa válida, nomeando a escolha.', 'Amplie para 3 opções quando houver consistência.'],
        registro: { colunas: ['Tentativa', 'Comunicou sozinho', 'Precisou de modelo'], linhas: 6 },
      },
    },
  }),
  ficha({
    id: 'pronta_soc_autorregulacao_termometro',
    titulo: 'Termômetro das emoções',
    eixo: 'social',
    habilidades_alvo: ['soc_autorregulacao'],
    faixa_etaria: { min: 4, max: 12 },
    nivel_dificuldade: 1,
    objetivo: 'Identificar o próprio estado emocional e escolher uma estratégia de retorno à calma.',
    instrucao: 'Peça para o aluno apontar em qual faixa do termômetro ele está agora e escolher uma estratégia da lista.',
    impressao: {
      tipo: 'escala_visual',
      config: {
        faixas: [
          { cor: '#bbf7d0', rotulo: 'Calmo' },
          { cor: '#fde68a', rotulo: 'Alerta' },
          { cor: '#fca5a5', rotulo: 'Agitado' },
        ],
        estrategias: ['Respirar fundo 3 vezes.', 'Apertar uma bolinha antiestresse.', 'Pedir para sair um minuto do lugar.', 'Pedir ajuda para um adulto de confiança.'],
      },
    },
  }),
  ficha({
    id: 'pronta_soc_autonomia_rotina',
    titulo: 'Meu quadro de rotina',
    eixo: 'social',
    habilidades_alvo: ['soc_autonomia'],
    faixa_etaria: { min: 4, max: 12 },
    nivel_dificuldade: 1,
    objetivo: 'Seguir uma sequência de rotina com independência crescente.',
    instrucao: 'Peça para marcar cada etapa da rotina assim que for concluída, na ordem da lista.',
    impressao: {
      tipo: 'roteiro_aplicacao',
      config: {
        passos: ['Guardar o material na mochila.', 'Pendurar a mochila no lugar certo.', 'Lavar as mãos.', 'Sentar no lugar para começar a atividade.'],
        registro: { colunas: ['Etapa', 'Concluiu sozinho', 'Com lembrete'], linhas: ['Guardar material', 'Pendurar mochila', 'Lavar as mãos', 'Sentar no lugar'] },
      },
    },
  }),
];

export default atividadesProntas;
